'use client'

import { DxpModelsInvestmentManagerDto } from '@dxp/data-access/customer'
import { Box, Card, Link, Skeleton, Typography } from '@mui/material'
import clsx from 'clsx'
import { ReactNode, useState } from 'react'

import { Icon } from '../icon'
import { QCLogoRound } from '../logo/logo'

import styles from './styles.module.scss'

function InvestmentManagerCardUI({
  profileImage,
  name,
  email,
  title,
  telephone,
  location,
  variant,
  lessSpace = false,
}: {
  profileImage: ReactNode
  name: ReactNode
  email: ReactNode
  title: ReactNode
  telephone: ReactNode
  location: ReactNode
  variant?: 'light' | 'dark'
  lessSpace?: boolean
}) {
  return (
    <Card
      className={clsx(
        styles.root,
        variant === 'dark' && styles.dark,
        lessSpace && styles.lessSpace,
      )}
    >
      <div className={styles.profile}>
        {profileImage}
        <Typography
          data-testid="investment-manager-name"
          className={styles.profileName}
          color="inherit"
          component="span"
          variant="h6"
        >
          {name}
        </Typography>
        <Typography
          className={styles.profileTitle}
          color="inherit"
          component="span"
          variant="h5"
        >
          {title}
        </Typography>
      </div>
      <div className={styles.details}>
        {telephone}
        {email}
        {location}
      </div>
    </Card>
  )
}

export function InvestmentManagerCardSkeleton() {
  return (
    <InvestmentManagerCardUI
      profileImage={<Skeleton variant="circular" height={96} width={96} />}
      name={
        <Skeleton
          variant="text"
          height={34}
          width={200}
          sx={{ transform: 'scale(1)' }}
        />
      }
      title={
        <Skeleton
          variant="text"
          height={34}
          width={200}
          sx={{ transform: 'scale(1)' }}
        />
      }
      telephone={
        <Skeleton height={24} width={500} sx={{ transform: 'scale(1)' }} />
      }
      email={
        <Skeleton height={24} width={500} sx={{ transform: 'scale(1)' }} />
      }
      location={
        <Skeleton height={92} width={500} sx={{ transform: 'scale(1)' }} />
      }
    />
  )
}

export function InvestmentManagerCard({
  investmentManager: im,
  variant,
  lessSpace = false,
}: {
  variant?: 'light' | 'dark'
  investmentManager: DxpModelsInvestmentManagerDto
  lessSpace?: boolean
}) {
  const [showFallback, setShowFallback] = useState(!im.profileImageUrl)
  const address = [
    im.addressLine1,
    im.addressLine2,
    im.addressLine3,
    im.city,
    im.country,
    im.postcode,
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <InvestmentManagerCardUI
      variant={variant}
      profileImage={
        showFallback ? (
          <QCLogoRound className={styles.placeholder} variant={variant} />
        ) : (
          <img
            onError={() => setShowFallback(true)}
            alt={im.name}
            src={im.profileImageUrl}
          />
        )
      }
      name={im.name}
      title="Investment Manager"
      telephone={
        <div role="group" aria-label={`Phone: ${im.phoneNumber}`}>
          <Icon icon="Telephone" ariaHidden />
          <Link
            data-testid="investment-manager-phone"
            color="inherit"
            href={`tel:${im.phoneNumber}`}
            underline="none"
          >
            {im.phoneNumber}
          </Link>
        </div>
      }
      email={
        <div role="group" aria-label={`Email: ${im.email}`}>
          <Icon icon="Send" ariaHidden />
          <Link
            data-testid="investment-manager-email"
            color="inherit"
            href={`mailto:${im.email}`}
            underline="none"
            className={styles.emailLink}
            data-email={im.email}
          >
            Email {im.name}
          </Link>
        </div>
      }
      location={
        <div role="text" aria-label={`Address: ${address}`}>
          <Icon icon="Location" ariaHidden />
          <Typography
            component="address"
            className={styles.address}
            data-testid="investment-manager-address"
          >
            {address}
          </Typography>
        </div>
      }
      lessSpace={lessSpace}
    />
  )
}

export function InvestmentManagerCards({
  investmentManagers: ims,
}: {
  investmentManagers?: DxpModelsInvestmentManagerDto[]
}) {
  if (!ims || ims?.length === 0) return null
  return (
    <Box
      gap={2}
      sx={{
        mt: 2,
        display: 'flex',
        flexDirection: {
          xs: 'column',
          sm: 'column',
          md: 'row',
        },
      }}
    >
      {ims.map(im => (
        <InvestmentManagerCard key={im.code} investmentManager={im} />
      ))}
    </Box>
  )
}
